import { createRouter, createWebHistory } from "vue-router"
import useSessionStore from "@/stores/sessionStore.js"
import useUserStore from "@/stores/userStore.js"
import setPageTitleFromRoute from "@/helpers/setPageTitleFromRoute.js"
import SettingsAccountView from "@/views/settingsTabs/SettingsAccountView.vue"
import AssetsAnalysisView from "@/views/assetsTabs/AssetsAnalysisView.vue"
import AssetsInformationView from "@/views/assetsTabs/AssetsInformationView.vue"
import AssetsView from "@/views/AssetsView.vue"
import SettingsBillingView from "@/views/settingsTabs/SettingsBillingView.vue"
import DocumentsInvoicesView from "@/views/documentsTabs/DocumentsInvoicesView.vue"
import ChangeEmailView from "@/views/ChangeEmailView.vue"
import MessagesContactView from "@/views/messagesTabs/MessagesContactView.vue"
import DashboardView from "@/views/DashboardView.vue"
import MessagesContactDisruptionView from "@/views/messagesTabs/MessagesContactDisruptionView.vue"
import DocumentsView from "@/views/DocumentsView.vue"
import EnergyBillView from "@/views/EnergyBillView.vue"
import EnergyHubView from "@/views/EnergyHubView.vue"
import DocumentsExportsView from "@/views/documentsTabs/DocumentsExportsView.vue"
import MessagesContactGeneralView from "@/views/messagesTabs/MessagesContactGeneralView.vue"
import InsightsView from "@/views/InsightsView.vue"
import LoginView from "@/views/LoginView.vue"
import LogoutView from "@/views/LogoutView.vue"
import MarketsView from "@/views/MarketsView.vue"
import MessagesView from "@/views/MessagesView.vue"
import MessagesOverviewView from "@/views/messagesTabs/MessagesOverviewView.vue"
import MessagesNotificationsView from "@/views/messagesTabs/MessagesNotificationsView.vue"
import PublicContactView from "@/views/PublicContactView.vue"
import RequestPasswordChangeView from "@/views/RequestPasswordChangeView.vue"
import SetPasswordView from "@/views/SetPasswordView.vue"
import SettingsView from "@/views/SettingsView.vue"

const routes = [
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { title: "Inloggen", onlyPublic: true, public: true },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    alias: "/welcome",
    component: RequestPasswordChangeView,
    meta: {
      title: {
        "/forgot-password": "Wachtwoord vergeten",
        "/welcome": "Welkom",
      },
      onlyPublic: true,
      public: true,
    },
  },
  {
    path: "/set-password",
    name: "set-password",
    alias: "/change-password",
    component: SetPasswordView,
    meta: { title: "Wachtwoord instellen", onlyPublic: true, public: true },
  },
  {
    path: "/contact",
    name: "contact",
    component: PublicContactView,
    meta: {
      title: "Contact",
      onlyPublic: true,
      public: true,
      authRedirect: "messages-contact",
    },
  },
  {
    path: "/messages",
    name: "messages",
    component: MessagesView,
    redirect: { name: "messages-notifications" },
    children: [
      {
        path: "overview",
        name: "messages-overview",
        component: MessagesOverviewView,
        meta: {
          title: "Berichten > Mijn berichten",
          flag: "can-see-my-messages",
        },
      },
      {
        path: "notifications",
        alias: ["/notifications", "/news"],
        name: "messages-notifications",
        component: MessagesNotificationsView,
        meta: { title: "Berichten > Notificaties" },
      },
      {
        path: "contact",
        name: "messages-contact",
        component: MessagesContactView,
        meta: { title: "Berichten > Contact" },
      },
      {
        path: "contact/general",
        name: "messages-contact-general",
        component: MessagesContactGeneralView,
        meta: { title: "Berichten > Algemene vraag" },
      },
      {
        path: "contact/disruption",
        name: "messages-contact-disruption",
        component: MessagesContactDisruptionView,
        meta: {
          title: "Berichten > Storingen en tijdelijke wijzigingen",
          flag: "can-see-contact-form",
        },
      },
    ],
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: DashboardView,
    meta: { title: "Dashboard", queryId: true },
  },
  {
    path: "/energy-bill",
    name: "energy-bill",
    component: EnergyBillView,
    meta: {
      title: "Energierekening",
      queryId: true,
    },
  },
  {
    path: "/insights",
    alias: ["/", "/results"],
    name: "insights",
    component: InsightsView,
    meta: {
      title: "Inzichten",
      queryId: true,
      queryDate: true,
      selectsAsset: true,
    },
  },
  {
    path: "/assets",
    name: "assets",
    component: AssetsView,
    redirect: { name: "assets-analysis" },
    children: [
      {
        path: "information",
        name: "assets-information",
        component: AssetsInformationView,
        meta: { title: "Installaties > Info", queryId: true },
      },
      {
        path: "analysis",
        name: "assets-analysis",
        component: AssetsAnalysisView,
        meta: {
          title: "Installaties > Meetgegevens",
          queryId: true,
          queryDate: true,
          selectsAsset: true,
        },
      },
    ],
  },
  {
    path: "/energy-hub",
    name: "energy-hub",
    component: EnergyHubView,
    meta: { title: "Energy Hub", queryId: true, queryDate: true },
  },
  {
    path: "/markets",
    name: "markets",
    component: MarketsView,
    meta: { title: "Energiemarkt", queryDate: true },
  },
  {
    path: "/change-email",
    name: "change-email",
    component: ChangeEmailView,
    props: { context: "user" },
    meta: { title: "E-mailadres wijzigen" },
  },
  {
    path: "/documents",
    name: "documents",
    component: DocumentsView,
    redirect: { name: "documents-invoices" },
    children: [
      {
        path: "invoices",
        name: "documents-invoices",
        component: DocumentsInvoicesView,
        meta: { title: "Documenten > Facturen" },
      },
      {
        path: "exports",
        name: "documents-exports",
        component: DocumentsExportsView,
        meta: { title: "Documenten > Exports" },
      },
    ],
  },
  {
    path: "/change-invoice-email",
    name: "change-invoice-email",
    component: ChangeEmailView,
    props: { context: "invoice" },
    meta: { title: "E-mailadres wijzigen" },
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsView,
    redirect: { name: "settings-account" },
    children: [
      {
        path: "account",
        name: "settings-account",
        component: SettingsAccountView,
        meta: { title: "Instellingen > Account" },
      },
      {
        path: "billing",
        name: "settings-billing",
        component: SettingsBillingView,
        meta: { title: "Instellingen > Facturering" },
      },
    ],
  },
  {
    path: "/logout",
    name: "logout",
    component: LogoutView,
    meta: { title: "Uitloggen", public: true },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "insights" },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach((to) => {
  const sessionStore = useSessionStore()
  if (sessionStore.unknown) {
    return undefined
  }
  // Redirect to login if unauthorized on non-public route
  if (!to.meta.public && !sessionStore.isAuthenticated) {
    return { name: "login" }
  }
  // Redirect if already logged in on only-public route
  if (to.meta.onlyPublic && sessionStore.isAuthenticated) {
    return { name: to.meta.authRedirect ?? "insights" }
  }
  // Redirect if user doesn't have required flag for route
  if (to.meta.flag && !useUserStore().hasFlag(to.meta.flag)) {
    return { name: "insights" }
  }

  // Navigation is validated
  return undefined
})

router.afterEach((to) => {
  setPageTitleFromRoute(to)
})

export default router
